import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { ChatResponse } from 'src/app/models/chat.model';
import { CHAT_TITLE_MAX_LENGTH, NEW_ROW_ANIMATION_MS } from 'src/app/shared/constants/chat.constants';
import { ChatRailGroup, ChatRailRow, groupChatsForRail } from './chat-rail-grouping';

@Component({
  standalone: false,
  selector: 'app-chat-history-rail',
  templateUrl: './chat-history-rail.component.html',
  styleUrls: ['./chat-history-rail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatHistoryRailComponent implements OnChanges, OnDestroy {
  @Input() chats: ChatResponse[] = [];
  @Input() activeChatId: string | null = null;

  @HostBinding('class.open')
  @Input()
  isOpenOnMobile = false;

  @Output() newChat = new EventEmitter<void>();
  @Output() chatSelected = new EventEmitter<string>();
  @Output() starToggled = new EventEmitter<{ id: string; isStarred: boolean }>();
  @Output() renameCommitted = new EventEmitter<{ id: string; title: string }>();
  @Output() deleteRequested = new EventEmitter<string>();

  readonly renameMaxLength = CHAT_TITLE_MAX_LENGTH;

  groups: ChatRailGroup[] = [];
  searchTerm = '';
  renamingChatId: string | null = null;
  renameInput = '';
  newlyAddedChatId: string | null = null;

  private previousChatIds = new Set<string>();
  private newRowTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild('renameField') set renameField(field: ElementRef<HTMLInputElement> | undefined) {
    if (field) {
      field.nativeElement.value = this.renameInput;
      field.nativeElement.focus();
      field.nativeElement.select();
    }
  }

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chats']) {
      this.trackNewlyAddedChat();
      this.recomputeGroups();
    }
  }

  ngOnDestroy(): void {
    if (this.newRowTimer) {
      clearTimeout(this.newRowTimer);
    }
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;
    this.recomputeGroups();
  }

  clearSearch(input: HTMLInputElement): void {
    input.value = '';
    this.onSearchInput('');
  }

  selectChat(chat: ChatResponse): void {
    if (this.renamingChatId === chat.id) {
      return;
    }
    this.chatSelected.emit(chat.id);
  }

  toggleStar(chat: ChatResponse, event: MouseEvent): void {
    event.stopPropagation();
    this.starToggled.emit({ id: chat.id, isStarred: !chat.isStarred });
  }

  startRenaming(chat: ChatResponse, event: MouseEvent, menuTrigger: MatMenuTrigger): void {
    event.stopPropagation();
    menuTrigger.closeMenu();
    this.renamingChatId = chat.id;
    this.renameInput = chat.title;
    this.cdr.markForCheck();
  }

  onRenameInput(event: Event): void {
    this.renameInput = (event.target as HTMLInputElement).value;
  }

  commitRename(chat: ChatResponse): void {
    if (this.renamingChatId !== chat.id) {
      return;
    }
    this.renamingChatId = null;
    const trimmed = this.renameInput.trim();
    if (trimmed && trimmed !== chat.title) {
      this.renameCommitted.emit({ id: chat.id, title: trimmed });
    }
    this.cdr.markForCheck();
  }

  cancelRenaming(): void {
    this.renamingChatId = null;
    this.renameInput = '';
    this.cdr.markForCheck();
  }

  requestDelete(chat: ChatResponse, event: MouseEvent): void {
    event.stopPropagation();
    this.deleteRequested.emit(chat.id);
  }

  trackByGroupName(_: number, group: ChatRailGroup): string {
    return group.name;
  }

  trackByRowId(_: number, row: ChatRailRow): string {
    return row.chat.id;
  }

  private recomputeGroups(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const filtered = term ? this.chats.filter((chat) => chat.title.toLowerCase().includes(term)) : this.chats;
    this.groups = groupChatsForRail(filtered, new Date());
  }

  private trackNewlyAddedChat(): void {
    const currentIds = new Set(this.chats.map((chat) => chat.id));
    const isFirstLoad = this.previousChatIds.size === 0;
    const addedIds = [...currentIds].filter((id) => !this.previousChatIds.has(id));
    this.previousChatIds = currentIds;

    if (this.newRowTimer) {
      clearTimeout(this.newRowTimer);
      this.newRowTimer = null;
    }

    if (isFirstLoad || addedIds.length !== 1) {
      this.newlyAddedChatId = null;
      return;
    }

    this.newlyAddedChatId = addedIds[0];
    this.newRowTimer = setTimeout(() => {
      this.newlyAddedChatId = null;
      this.newRowTimer = null;
      this.cdr.markForCheck();
    }, NEW_ROW_ANIMATION_MS);
  }
}
